/**
 * Services report as cards: highlights, one card per sub-service with its
 * share of earnings and a status bar, and the services with no orders at the end.
 */
import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, ListAlt as OrdersIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { ServiceReportRow } from '../../store/api/detailedReportsApi';
import { formatCurrency, formatNumber } from '../../utils/format';

interface Props {
  rows: ServiceReportRow[];
  name: (r: ServiceReportRow) => string;
  serviceName: (r: ServiceReportRow) => string;
  onOpen: (r: ServiceReportRow) => void;
  canExport: boolean;
  /** Service whose workbook is being downloaded */
  exportingId: number | null;
  onExport: (r: ServiceReportRow) => void;
}

const Highlight = ({
  label,
  title,
  value,
  color,
}: {
  label: string;
  title: string;
  value: ReactNode;
  color: string;
}) => (
  <Card sx={{ borderInlineStart: `4px solid ${color}` }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="subtitle1" fontWeight={700} noWrap title={title}>
        {title}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ color }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const Fact = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block" noWrap>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700}>
      {value}
    </Typography>
  </Box>
);

const ServicesCards = ({ rows, name, serviceName, onOpen, canExport, exportingId, onExport }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const k = (key: string, opts?: Record<string, unknown>) => t(`reports.detailed.services.${key}`, opts);

  const withOrders = rows.filter((r) => r.total_orders > 0);
  const idle = rows.filter((r) => r.total_orders === 0);
  const totalValue = withOrders.reduce((sum, r) => sum + r.completed_value, 0);

  const colors = {
    completed: theme.palette.success.main,
    open: theme.palette.info.main,
    cancelled: theme.palette.error.main,
  };

  // Highlights only make sense with a few orders behind them
  const ranked = withOrders.filter((r) => r.total_orders >= 3);
  const topValue = [...withOrders].sort((a, b) => b.completed_value - a.completed_value)[0];
  const topRating = [...ranked]
    .filter((r) => r.avg_service_rating != null)
    .sort((a, b) => (b.avg_service_rating ?? 0) - (a.avg_service_rating ?? 0))[0];
  const cancelRate = (r: ServiceReportRow) => (r.total_orders ? (r.cancelled_orders * 100) / r.total_orders : 0);
  const topCancel = [...ranked].filter((r) => r.cancelled_orders > 0).sort((a, b) => cancelRate(b) - cancelRate(a))[0];

  if (!rows.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
        {t('reports.detailed.noData')}
      </Typography>
    );
  }

  return (
    <Box>
      {(topValue || topRating || topCancel) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          {topValue && topValue.completed_value > 0 && (
            <Highlight
              label={k('topValue')}
              title={name(topValue)}
              value={formatCurrency(topValue.completed_value)}
              color={colors.completed}
            />
          )}
          {topRating && (
            <Highlight
              label={k('topRating')}
              title={name(topRating)}
              value={`★ ${topRating.avg_service_rating?.toFixed(1)} · ${k('ratingsN', { count: topRating.ratings_count })}`}
              color={theme.palette.warning.main}
            />
          )}
          {topCancel && (
            <Highlight
              label={k('topCancel')}
              title={name(topCancel)}
              value={k('cancelSummary', { rate: Math.round(cancelRate(topCancel)), count: topCancel.cancelled_orders })}
              color={colors.cancelled}
            />
          )}
        </Box>
      )}

      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2 }}
      >
        {withOrders.map((r) => {
          const share = totalValue ? Math.round((r.completed_value * 100) / totalValue) : 0;
          const open = r.pending_orders + r.active_orders;
          const segments = [
            { key: 'completed', count: r.completed_orders, color: colors.completed },
            { key: 'open', count: open, color: colors.open },
            { key: 'cancelled', count: r.cancelled_orders, color: colors.cancelled },
          ];
          return (
            <Card key={r.id} sx={{ opacity: r.is_active ? 1 : 0.75, display: 'flex', flexDirection: 'column' }}>
              <CardActionArea onClick={() => onOpen(r)} sx={{ flexGrow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {name(r)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {serviceName(r)}
                        {r.parent_name ? ` · ${r.parent_name}` : ''}
                      </Typography>
                    </Box>
                    {!r.is_active && <Chip size="small" label={t('reports.detailed.inactive')} />}
                  </Box>

                  <Box
                    sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mt: 1.5 }}
                  >
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {formatCurrency(r.completed_value)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {k('share', { percent: share })}
                    </Typography>
                  </Box>
                  {/* Always takes its line so the cards stay aligned */}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {r.open_value > 0 ? k('openValue', { value: formatCurrency(r.open_value) }) : ' '}
                  </Typography>

                  {/* Orders by status */}
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {k('ordersN', { count: r.total_orders })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {k('completionRate', { rate: r.completion_rate ?? 0 })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: 'action.hover' }}
                    >
                      {segments.map((s) =>
                        s.count ? (
                          <Tooltip key={s.key} title={`${k(`status.${s.key}`)}: ${s.count}`}>
                            <Box sx={{ width: `${(s.count * 100) / r.total_orders}%`, bgcolor: s.color }} />
                          </Tooltip>
                        ) : null,
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
                      {segments.map((s) => (
                        <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                          <Typography variant="caption" color="text.secondary">
                            {k(`status.${s.key}`)} {formatNumber(s.count)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: 1,
                      mt: 2,
                      pt: 1.5,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Fact label={k('patients')} value={formatNumber(r.patients_count)} />
                    <Fact label={k('doctors')} value={formatNumber(r.providers_count)} />
                    <Fact
                      label={k('serviceRating')}
                      value={r.avg_service_rating != null ? `★ ${r.avg_service_rating.toFixed(1)}` : '—'}
                    />
                    <Fact
                      label={k('doctorRating')}
                      value={r.avg_provider_rating != null ? `★ ${r.avg_provider_rating.toFixed(1)}` : '—'}
                    />
                  </Box>
                </CardContent>
              </CardActionArea>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Button size="small" startIcon={<OrdersIcon />} onClick={() => onOpen(r)}>
                  {k('showOrders')}
                </Button>
                {canExport && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={exportingId !== null}
                    startIcon={exportingId === r.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                    onClick={() => onExport(r)}
                  >
                    {k('exportOne')}
                  </Button>
                )}
              </Box>
            </Card>
          );
        })}
      </Box>

      {idle.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {k('idleTitle', { count: idle.length })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {idle.map((r) => (
              <Chip key={r.id} variant="outlined" label={`${name(r)} · ${serviceName(r)}`} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ServicesCards;
